/**
 * The draft row for adding a binding, ported from t3code.
 *
 * Lives in the table rather than in a dialog so the chord being chosen sits in
 * the same column as the chords it must not collide with — the conflict warning
 * is only useful next to what it is warning about.
 *
 * @module
 */
import type { KeybindingCommand, KeybindingWhenNode } from "@cmmd-forge/contracts";
import { useState } from "react";

import { Button } from "../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { whenAstToExpression } from "./keybindingWhenDraft";
import { commandLabel, type KeybindingRow } from "./KeybindingsSettings.logic";
import { KeybindingShortcutRecorder } from "./KeybindingShortcutRecorder";
import {
  KEYBINDING_ROW_CLASSNAME,
  KEYBINDING_ROW_COLUMNS,
  KeybindingConflictWarning,
} from "./KeybindingTableRow";
import { WhenExpressionBuilder } from "./WhenExpressionBuilder";

export interface NewKeybindingTableRowProps {
  readonly commandOptions: ReadonlyArray<KeybindingCommand>;
  readonly variables: ReadonlyArray<string>;
  /** Every existing row, so a clash is visible before the binding is saved. */
  readonly rows: ReadonlyArray<KeybindingRow>;
  readonly isSaving: boolean;
  readonly onSave: (input: {
    readonly command: KeybindingCommand;
    readonly key: string;
    readonly when: string | undefined;
  }) => void;
  readonly onCancel: () => void;
  readonly platform?: string | undefined;
}

/** A stand-in row, so the recorder can render before a command is chosen. */
function draftRow(command: KeybindingCommand | "", key: string, when: string): KeybindingRow {
  return {
    id: "keybinding-new",
    command: (command || "chat.new") as KeybindingCommand,
    key,
    shortcut: {
      key: "",
      modKey: false,
      metaKey: false,
      ctrlKey: false,
      altKey: false,
      shiftKey: false,
    },
    when,
    whenAst: undefined,
    source: "Custom",
    defaultKeys: [],
    conflicts: [],
  };
}

export function NewKeybindingTableRow({
  commandOptions,
  variables,
  rows,
  isSaving,
  onSave,
  onCancel,
  platform,
}: NewKeybindingTableRowProps) {
  const [command, setCommand] = useState<KeybindingCommand | "">("");
  const [key, setKey] = useState("");
  const [when, setWhen] = useState<KeybindingWhenNode | undefined>(undefined);
  const [isWhenValid, setIsWhenValid] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isWhenOpen, setIsWhenOpen] = useState(false);

  const whenText = whenAstToExpression(when);
  // Compared against the same chord-plus-context pair the resolver uses, so the
  // warning matches what would actually happen rather than just the chord.
  const conflicts = rows
    .filter((existing) => existing.key === key && existing.when === whenText)
    .map((existing) => existing.command);

  const canSave = command !== "" && key.length > 0 && isWhenValid && !isSaving;

  return (
    <li className={`${KEYBINDING_ROW_CLASSNAME} ${KEYBINDING_ROW_COLUMNS} bg-accent/20`}>
      <div className="min-w-0 pr-4">
        <Select
          value={command}
          onValueChange={(next) => next && setCommand(next as KeybindingCommand)}
        >
          <SelectTrigger aria-label="Command" className="w-full" size="xs">
            <SelectValue placeholder="Choose a command" />
          </SelectTrigger>
          <SelectContent alignItemWithTrigger={false} className="max-h-72 w-fit min-w-56">
            {commandOptions.map((option) => (
              <SelectItem className="min-h-7 py-1 text-xs" key={option} value={option}>
                {commandLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex min-w-0 items-center gap-2 pr-4">
        <KeybindingShortcutRecorder
          isRecording={isRecording}
          onCancelRecording={() => setIsRecording(false)}
          onRecorded={(shortcut) => {
            setKey(shortcut);
            setIsRecording(false);
          }}
          onStartRecording={() => setIsRecording(true)}
          platform={platform}
          row={draftRow(command, key, whenText)}
        />
      </div>

      <div className="pr-4">
        <Popover onOpenChange={setIsWhenOpen} open={isWhenOpen}>
          <PopoverTrigger
            render={
              <button
                aria-label="Set when clause"
                className="inline-flex h-7 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 text-left font-mono text-foreground text-xs outline-none transition-colors hover:bg-accent focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/24"
                type="button"
              />
            }
          >
            <span className={whenText === "" ? "truncate text-muted-foreground" : "truncate"}>
              {whenText === "" ? "Always" : whenText}
            </span>
          </PopoverTrigger>
          <PopoverPopup align="start" className="w-96 max-w-[calc(100vw-2rem)] p-3">
            <WhenExpressionBuilder
              onChange={setWhen}
              onValidityChange={setIsWhenValid}
              value={when}
              variables={variables}
            />
            <div className="mt-3 flex justify-end">
              <Button onClick={() => setIsWhenOpen(false)} size="xs" type="button" variant="ghost">
                Done
              </Button>
            </div>
          </PopoverPopup>
        </Popover>
      </div>

      <div className="flex items-center justify-end gap-1">
        <KeybindingConflictWarning conflicts={conflicts} />
        <Button onClick={onCancel} size="xs" type="button" variant="ghost">
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          onClick={() => {
            if (command === "") return;
            onSave({ command, key, when: whenText === "" ? undefined : whenText });
          }}
          size="xs"
          type="button"
        >
          Save
        </Button>
      </div>
    </li>
  );
}
